import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationActions } from './notification.actions';
import {
  catchError,
  filter,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';
import {
  NOTIFICATION_STATE_SERVICE_INJECTION_TOKEN,
  NotificationStateService,
} from '../../../core/services/api/notification-state-service.interface';
import {
  selectLatestNotifications,
  selectNotificationFilter,
  selectNotificationsUnreadCount,
} from './notification.selectors';
import { NotificationLevel } from '@birco/rainmesh-api';
import { NotificationToastService } from '../services/notification-toast.service';

@Injectable()
export class NotificationEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private notificationStateService = inject<NotificationStateService>(
    NOTIFICATION_STATE_SERVICE_INJECTION_TOKEN,
  );
  private notificationToastService = inject(NotificationToastService);

  public updateNotificationFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.public.updateNotificationFilter),
      map((_props) =>
        NotificationActions.private.loadNotificationsWithFilters(),
      ),
    ),
  );

  public loadLatestNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.public.loadLatestNotifications),
      switchMap(() =>
        this.notificationStateService
          .getNotifications({
            page: 0,
            pageSize: 10,
          })
          .pipe(
            map((responseDto) =>
              NotificationActions.private.loadNotificationsWithProvidedFiltersSuccess(
                {
                  responseDto,
                  updateUnreadCount: true,
                },
              ),
            ),
            catchError((error) =>
              of(
                NotificationActions.private.loadNotificationsWithProvidedFiltersFailure(
                  {
                    error,
                  },
                ),
              ),
            ),
          ),
      ),
    ),
  );

  public loadUnreadErrorNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.public.loadUnreadErrorNotifications),
      switchMap(() =>
        this.notificationStateService
          .getNotifications({
            page: 0,
            pageSize: 100, // todo load all
            level: NotificationLevel.ERROR,
          })
          .pipe(
            map((responseDto) =>
              NotificationActions.private.loadNotificationsWithProvidedFiltersSuccess(
                {
                  responseDto,
                },
              ),
            ),
            catchError((error) =>
              of(
                NotificationActions.private.loadNotificationsWithProvidedFiltersFailure(
                  {
                    error,
                  },
                ),
              ),
            ),
          ),
      ),
    ),
  );

  public loadNotificationsWithFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.private.loadNotificationsWithFilters),
        withLatestFrom(this.store.select(selectNotificationFilter)),
        switchMap(([_action, filter]) =>
          this.notificationStateService.getNotifications(filter).pipe(
            map((notificationsResponse) =>
              NotificationActions.private.loadNotificationsSuccess({
                notificationsResponseDto: notificationsResponse,
              }),
            ),
            catchError((error) =>
              of(
                NotificationActions.private.loadNotificationsFailure({ error }),
              ),
            ),
          ),
        ),
      ),
    { dispatch: true },
  );

  public markAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.public.markAsRead),
        switchMap((props) =>
          this.notificationStateService
            .markAsRead({
              id: props.id,
            })
            .pipe(
              map((notificationResponse) =>
                NotificationActions.private.markAsReadSuccess({
                  responseDto: notificationResponse,
                }),
              ),
              catchError((e) =>
                of(
                  NotificationActions.private.markAsReadFailure({
                    id: 'TODO',
                    error: e,
                  }),
                ),
              ),
            ),
        ),
      ),
    { dispatch: true },
  );

  public markAsReadSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.private.markAsReadSuccess),
        withLatestFrom(this.store.select(selectNotificationsUnreadCount)),
        map(([_, unreadCount]) =>
          NotificationActions.private.updateUnreadCount({
            unreadCount: unreadCount - 1,
          }),
        ),
      ),
    { dispatch: true },
  );

  public markAllAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.public.markAllAsRead),
        switchMap((_props) =>
          this.notificationStateService.markAllAsRead().pipe(
            map((_notificationResponse) =>
              NotificationActions.private.markAllAsReadSuccess(),
            ),
            catchError((e) =>
              of(
                NotificationActions.private.markAllAsReadFailure({
                  error: e,
                }),
              ),
            ),
          ),
        ),
      ),
    { dispatch: true },
  );

  public markAllAsReadSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.private.markAllAsReadSuccess),
        map(() =>
          NotificationActions.private.updateUnreadCount({
            unreadCount: 0,
          }),
        ),
      ),
    { dispatch: true },
  );

  public deleteAll$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.public.deleteAll),
        switchMap((_props) =>
          this.notificationStateService.deleteAll().pipe(
            map(() => NotificationActions.private.deleteAllSuccess()),
            catchError((e) =>
              of(
                NotificationActions.private.deleteAllFailure({
                  error: e,
                }),
              ),
            ),
          ),
        ),
      ),
    { dispatch: true },
  );

  public deleteAllSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.private.deleteAllSuccess),
        map(() =>
          NotificationActions.private.updateUnreadCount({
            unreadCount: 0,
          }),
        ),
      ),
    { dispatch: true },
  );

  public loadNotificationsWithFiltersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.private.loadNotificationsSuccess),
      map(({ notificationsResponseDto }) =>
        NotificationActions.private.updateUnreadCount({
          unreadCount: notificationsResponseDto.unreadCount,
        }),
      ),
    ),
  );

  loadNotificationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NotificationActions.private.loadNotificationsWithProvidedFiltersSuccess,
      ),
      filter(({ updateUnreadCount }) => !!updateUnreadCount),
      map(({ responseDto }) =>
        NotificationActions.private.updateUnreadCount({
          unreadCount: responseDto.unreadCount,
        }),
      ),
    ),
  );

  public refreshNotificationDataFromWebSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.public.refreshNotificationDataFromWebSocket),
      withLatestFrom(this.store.select(selectLatestNotifications)),
      switchMap(([{ notification }, latestNotifications]) =>
        this.notificationStateService
          .getNotifications({
            page: 0,
            pageSize: 10,
          })
          .pipe(
            map((responseDto) =>
              NotificationActions.private.loadNotificationsWithProvidedFiltersSuccess(
                {
                  responseDto,
                  updateUnreadCount: true,
                  knownNotificationIds: latestNotifications
                    .map((it) => it.id)
                    .filter((id): id is string => !!id),
                  websocketNotificationId: notification.id,
                },
              ),
            ),
            catchError((error) =>
              of(
                NotificationActions.private.loadNotificationsWithProvidedFiltersFailure(
                  {
                    error,
                  },
                ),
              ),
            ),
          ),
      ),
    ),
  );

  public showToastOnWebSocketNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NotificationActions.private
            .loadNotificationsWithProvidedFiltersSuccess,
        ),
        filter(({ websocketNotificationId }) => !!websocketNotificationId),
        map(
          ({ responseDto, websocketNotificationId, knownNotificationIds }) => ({
            knownNotificationIds: knownNotificationIds ?? [],
            notification: responseDto.notifications.find(
              (it) => it.id === websocketNotificationId,
            ),
          }),
        ),
        filter(
          ({ notification, knownNotificationIds }) =>
            !!notification &&
            !notification.readAt &&
            notification.level !== NotificationLevel.INFO &&
            !knownNotificationIds.includes(notification.id ?? ''),
        ),
        tap(({ notification }) =>
          this.notificationToastService.show(notification!),
        ),
      ),
    { dispatch: false },
  );
}
